import { Link } from "react-router-dom";
import type { Job } from "@/lib/mockData";
import { StatusDot } from "@/components/ui/StatusDot";
import { fmtElapsed } from "@/lib/format";
import { jobStatusLabel } from "@/lib/jobStatus";

interface Props {
  job: Job;
  size?: "s" | "m" | "l";
}

const SIZES = {
  s: "min-w-[150px] h-[70px]",
  m: "min-w-[200px] h-[88px]",
  l: "min-w-[260px] h-[110px]",
};

const BORDER_COLOR: Record<Job["status"], string> = {
  running: "var(--status-running)",
  approval: "var(--status-approval)",
  takeover: "var(--status-takeover)",
  failed: "var(--status-failed)",
  success: "var(--status-success)",
  human: "var(--status-human)",
  queued: "var(--status-queued)",
};

export function JobTile({ job, size = "m" }: Props) {
  // The dot's colour alone cannot tell "Waiting for a person" from
  // "Supervisor in control" (one colour) and is not readable by
  // everyone: the words are on the tile.
  const label = jobStatusLabel(job.status, job.apiStatus);
  return (
    <Link
      to={`/jobs/${job.id}`}
      aria-label={`${job.workflow.name}: ${label}`}
      className={[
        "group relative flex flex-col justify-between rounded-md border border-paper-line bg-paper-2 p-3 shadow-sm transition-shadow hover:shadow-md",
        SIZES[size],
      ].join(" ")}
      style={{ borderLeft: `3px solid ${BORDER_COLOR[job.status]}` }}
    >
      <div className="flex min-w-0 items-center gap-2">
        <StatusDot status={job.status} size={8} />
        <span className="truncate text-sm font-medium text-ink-1">{job.workflow.name}</span>
      </div>
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="min-w-0 truncate">
          <span className="text-ink-2" data-testid="tile-status">
            {label}
          </span>
          <span className="font-mono text-ink-3"> · {fmtElapsed(job.elapsed)}</span>
        </span>
        <span className="shrink-0 uppercase tracking-wider text-ink-4">{job.entity.name}</span>
      </div>

      {/* Hover card */}
      <div className="pointer-events-none absolute left-0 top-full z-10 mt-1 hidden w-[280px] rounded-md border border-paper-line-strong bg-paper-3 p-3 text-xs shadow-lg group-hover:block">
        <Row label="Status">
          <span>{label}</span>
        </Row>
        <Row label="Phase">
          <span className="truncate">
            {job.phases[job.curPhase] ?? "—"}
          </span>
        </Row>
        {job.patient && (
          <Row label="Patient">
            <span>
              {job.patient.name}{" "}
              <span className="font-mono text-ink-4">· MRN {job.patient.mrn}</span>
            </span>
          </Row>
        )}
        <Row label="Queued by">
          <span>{job.user.name}</span>
        </Row>
        <Row label="ID">
          <span className="font-mono text-ink-3">{job.id}</span>
        </Row>
      </div>
    </Link>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2 py-0.5">
      <span className="w-[70px] shrink-0 text-[11px] uppercase tracking-wider text-ink-4">
        {label}
      </span>
      <span className="min-w-0 flex-1 truncate text-ink-1">{children}</span>
    </div>
  );
}
